import React, { useEffect, useRef, useState } from "react";
import {
    Editor,
    EditorState,
    RichUtils,
    ContentState,
    convertFromHTML,
} from "draft-js";
import "draft-js/dist/Draft.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBold, faItalic, faUnderline, faSave, faMagic } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import axios from "axios";
import { stateToHTML } from "draft-js-export-html";

const API_URL = process.env.REACT_APP_API_URL;
const isAiActive = process.env.REACT_APP_YZACTIVE === "1";
const GET_SURVEY_IMPROVEMENT_DB = `${API_URL}/Custom/GetSurveyImprovementFromDb`;
const POST_SURVEY_IMPROVEMENT = `${API_URL}/Custom/PostSurveyImprovement`;
const GET_SURVEY_IMPROVEMENT_AI = `${API_URL}/Custom/GetSurveyImprovementFromAI`;

interface Props {
    semesterCourseId: number;
}

const SurveyImprovementReportComponent: React.FC<Props> = ({ semesterCourseId }) => {
    const [loading, setLoading] = useState(false);
    const [editorState, setEditorState] = useState(
        EditorState.createWithContent(ContentState.createFromText(""))
    );
    const [error, setError] = useState<string | null>(null);

    const editorRef = useRef<Editor | null>(null);

    useEffect(() => {
        loadSavedReport();
        focusEditor();
        // eslint-disable-next-line
    }, [semesterCourseId]);

    const focusEditor = () => {
        setTimeout(() => editorRef.current && editorRef.current.focus(), 100);
    };

    const loadSavedReport = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.post(GET_SURVEY_IMPROVEMENT_DB, semesterCourseId, {
                headers: { "Content-Type": "application/json" },
            });
            if (res.data?.report) {
                const blocks = convertFromHTML(res.data.report);
                const contentState = ContentState.createFromBlockArray(
                    blocks.contentBlocks,
                    blocks.entityMap
                );
                setEditorState(EditorState.createWithContent(contentState));
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const saveReport = async () => {
        const htmlContent = stateToHTML(editorState.getCurrentContent());
        try {
            const res = await axios.post(
                POST_SURVEY_IMPROVEMENT,
                {
                    semesterCourseId,
                    reportContent: htmlContent,
                },
                { headers: { "Content-Type": "application/json" } }
            );
            if (res.status === 200) {
                Swal.fire({
                    icon: "success",
                    title: "Kaydedildi",
                    timer: 1500,
                    showConfirmButton: false,
                });
            }
        } catch (err: any) {
            Swal.fire({ icon: "error", title: "Kaydedilemedi", text: err.message });
        }
    };

    const handleKeyCommand = (command: string) => {
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
            setEditorState(newState);
            return "handled";
        }
        return "not-handled";
    };

    const toggleStyle = (style: string) => (e: React.MouseEvent) => {
        e.preventDefault();
        setEditorState(RichUtils.toggleInlineStyle(editorState, style));
    };

    const clearForm = () => {
        setEditorState(EditorState.createWithContent(ContentState.createFromText("")));
    };

    const askAI = () => {
        Swal.fire({
            title: "Emin misiniz?",
            text: "Varsa mevcut içerik silinip yapay zeka tarafından doldurulacaktır. Bu özellik ders başına 1 kez kullanılabilir.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Evet",
            cancelButtonText: "Hayır",
        }).then(async (r) => {
            if (r.isConfirmed) {
                clearForm();
                await getAIReport();
            }
        });
    };

    const getAIReport = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.post(
                GET_SURVEY_IMPROVEMENT_AI,
                semesterCourseId,
                { headers: { "Content-Type": "application/json" } }
            );
            if (res.data?.response) {
                const html = res.data.response
                    .replace(/### (.*?)\n/g, "<h3>$1</h3>")
                    .replace(/#/g, "<br/>")
                    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
                    .replace(/\n{2,}/g, "</p><p>")
                    .replace(/\n/g, "<br/>");
                const blocks = convertFromHTML(`<p>${html}</p>`);
                const cs = ContentState.createFromBlockArray(blocks.contentBlocks, blocks.entityMap);
                setEditorState(EditorState.createWithContent(cs));
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const currentStyle = editorState.getCurrentInlineStyle();

    return (
        <div className="mt-5">
            <h5>🛠 İyileştirme Planı</h5>
            <p className="text-muted">
                Bu alana anket sonuçlarını dikkate alarak ders için yapacağınız iyileştirmeleri yazdıktan sonra kaydetmeniz beklenmektedir.
                {isAiActive && " Yapay zeka desteği almak için YZ butonunu kullanabilirsiniz."}
            </p>

            <div className="toolbar mb-2">
                <button
                    className={`btn btn-sm me-2 ${currentStyle.has("BOLD") ? "btn-dark" : "btn-secondary"}`}
                    onMouseDown={toggleStyle("BOLD")}
                >
                    <FontAwesomeIcon icon={faBold} />
                </button>
                <button
                    className={`btn btn-sm me-2 ${currentStyle.has("ITALIC") ? "btn-dark" : "btn-secondary"}`}
                    onMouseDown={toggleStyle("ITALIC")}
                >
                    <FontAwesomeIcon icon={faItalic} />
                </button>
                <button
                    className={`btn btn-sm me-2 ${currentStyle.has("UNDERLINE") ? "btn-dark" : "btn-secondary"}`}
                    onMouseDown={toggleStyle("UNDERLINE")}
                >
                    <FontAwesomeIcon icon={faUnderline} />
                </button>
                {isAiActive && (
                    <button onClick={askAI} className="btn btn-sm btn-warning me-2">
                        <FontAwesomeIcon icon={faMagic} /> YZ
                    </button>
                )}
                <button onClick={saveReport} className="btn btn-sm btn-success">
                    <FontAwesomeIcon icon={faSave} /> Kaydet
                </button>
            </div>

            <div
                className="border rounded p-2"
                style={{ minHeight: 180, cursor: "text", background: "#fff" }}
                onClick={focusEditor}
            >
                {loading && <div>Yükleniyor...</div>}
                {error && <div className="alert alert-danger">{error}</div>}
                <Editor
                    ref={editorRef}
                    editorState={editorState}
                    onChange={setEditorState}
                    handleKeyCommand={handleKeyCommand}
                    placeholder="İyileştirme raporunuzu buraya yazın..."
                />
            </div>
        </div>
    );
};

export default SurveyImprovementReportComponent;
